/**
 * feat-004 story 2.4 (AD-6 frontend mirror) — minimal capability-gating stub.
 *
 * No permission/capability system exists anywhere in this repo yet (verified
 * this session — zero hits for "capability" outside this feature's own
 * planning docs). This hook exists so `DisplayOptionsCard` has ONE swappable
 * gating point, mirroring the backend's `display-option-capabilities.ts`
 * stub philosophy: today it always allows (when no capability set is
 * injected), a future real permission model plugs in here without touching
 * component code.
 *
 * Keys mirror `Planigo/src/support/display-options/display-option-capabilities.ts`
 * verbatim — do not invent parallel key names.
 */
export const DisplayOptionCapability = {
  Create: 'displayOption.create',
  Edit: 'displayOption.edit',
  Archive: 'displayOption.archive',
  SetDefault: 'displayOption.setDefault',
  Enable: 'displayOption.enable',
  Disable: 'displayOption.disable',
  Delete: 'displayOption.delete',
  // feat-004 story 2.6 (mirrors backend's display-option-capabilities.ts verbatim).
  ImageUpload: 'displayOption.image.upload',
} as const

export type DisplayOptionCapabilityKey =
  (typeof DisplayOptionCapability)[keyof typeof DisplayOptionCapability]

/** A caller-injected set/map of the capabilities granted to the current user. */
export type CapabilitySet =
  | Set<string>
  | Record<string, boolean>
  | undefined
  | null

/**
 * Returns whether `key` is granted. With no `capabilities` supplied (the
 * default), every key is allowed — matching the backend stub's "always
 * allow, log only" stance. Once a real capability set is injected (e.g. by
 * a future host), only keys present (Set) / truthy (Record) are granted.
 */
export function useCapability(
  key: string,
  capabilities?: CapabilitySet
): boolean {
  if (capabilities == null) {
    return true
  }
  if (capabilities instanceof Set) {
    return capabilities.has(key)
  }
  return Boolean(capabilities[key])
}
